import { Outlet, useMatch, useNavigate } from 'react-router';

import { type INavigationItem } from '@workinance/shared/ui/AppShellLayoutComponent';
import { PersonIcon } from '@workinance/shared/ui/icons';
import { NotificationBellComponent } from '@workinance/shared/ui/NotificationBellComponent';
import {
	SideNavLayoutComponent,
	type INavigationSection,
} from '@workinance/shared/ui/SideNavLayoutComponent';
import { UserMenuComponent } from '@workinance/shared/ui/UserMenuComponent';
import { getUserTypeLabel } from '@workinance/shared/utility/user';

import { BusinessSwitcherComponent } from '@/components/dashboard/BusinessSwitcherComponent';
import {
	primaryNavigation,
	rolesNavigationItem,
	usersNavigationItem,
	valueListsNavigationItem,
} from '@/components/dashboard/navigation';
import { hasPermission, useAuthStore } from '@/store/auth.store';

export function DashboardLayoutComponent() {
	const navigate = useNavigate();
	// The POS screen draws its own bar and fills the window, so it goes without the dashboard's frame.
	const isPosScreen = useMatch({ path: '/pos', end: true }) !== null;
	const user = useAuthStore((state) => state.user);
	const permissions = useAuthStore((state) => state.permissions);
	const business = useAuthStore((state) => state.business);
	const logout = useAuthStore((state) => state.logout);

	// `.filter()` over a list can't call the `useCanView` hook per item, so this uses the plain check
	// it's built on directly, with the state read once above.
	const canSee = (item: INavigationItem) =>
		hasPermission({ user, permissions }, item.requiredPermissions) &&
		// The POS link only earns its place once the business has turned POS on for itself.
		(item.to !== '/pos' || business?.settings.pos.isEnabled);
	// A group's own visibility comes entirely from whether any of its children pass -- it carries
	// no permissions of its own, so there's nothing to check on the parent directly.
	const filterVisible = (items: INavigationItem[]): INavigationItem[] =>
		items.flatMap((item) => {
			if (!item.children) return canSee(item) ? [item] : [];
			const children = filterVisible(item.children);
			return children.length > 0 ? [{ ...item, children }] : [];
		});
	const visiblePrimaryNavigation = filterVisible(primaryNavigation);
	const teamItems = filterVisible([usersNavigationItem, rolesNavigationItem]);
	const settingsItems = filterVisible([valueListsNavigationItem]);
	const navigation: INavigationSection[] = [
		{ items: visiblePrimaryNavigation },
		...(teamItems.length > 0 ? [{ title: 'Team', items: teamItems }] : []),
		...(settingsItems.length > 0
			? [{ title: 'Settings', items: settingsItems }]
			: []),
	];

	const handleLogout = () => {
		logout();
		navigate('/login', { replace: true });
	};

	if (isPosScreen) return <Outlet />;

	return (
		<SideNavLayoutComponent
			homePath="/dashboard"
			navigation={navigation}
			headerCenter={<BusinessSwitcherComponent />}
			headerEnd={
				<>
					<NotificationBellComponent />
					<UserMenuComponent
						firstName={user?.firstName}
						surName={user?.surName}
						subtitle={getUserTypeLabel(user) || undefined}
						items={[
							{
								label: 'Your profile',
								icon: PersonIcon,
								onSelect: () => navigate('/profile'),
							},
						]}
						onLogout={handleLogout}
					/>
				</>
			}
		>
			<Outlet />
		</SideNavLayoutComponent>
	);
}
